import { styled } from "@pigment-css/react";
import type { Trend as TrendSchema } from "@acto/genui-components-schema";
import { useFlagWithContext } from "@acto/web-common/feature-flags";
import { useTranslations } from "@acto/web-common/locales";
import {
  COMPARISON_SERIES_FILL,
  LEAD_SERIES_FILL,
  TrendBarChart,
} from "@acto/taskpane/field-sales-2/components/primitives/TrendBarChart";
import { TrendComparison } from "@acto/taskpane/field-sales-2/components/primitives/TrendComparison";
import { hasTrend, trendSentiment } from "@acto/taskpane/field-sales-2/components/primitives/TrendPercent";
import { Label, Marker } from "@acto/taskpane/field-sales-2/components/primitives/KpiPairCard/KpiFigure";
import {
  formatCurrency,
  formatMonthYearRange,
  formatNumericDayMonthRange,
  formatPercentDelta,
} from "@acto/taskpane/field-sales-2/utils/format";
import { addMonthsUtc, toIsoDate } from "@acto/taskpane/field-sales-2/components/primitives/Calendar/dateUtils";
import {
  Card,
  CardHeadline,
  FooterCol,
  Header,
  KpiValue,
  MetaLabel,
  Section,
  SubLabel,
} from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { SentimentColor } from "./SentimentColor";
import { useChartDisplay } from "./useChartDisplay";

const PERIOD_KEY = "__periodStart";

const missing = (value: unknown) => value === null || value === undefined;

/**
 * Whether the chart's bands cover both windows rather than the current one alone, which the schema
 * does not state. Such a chart opens in the comparison window and closes in the current one, so its
 * first band holds no lead value and its last holds no comparison value. YoY bands are the current
 * window throughout and carry both series everywhere.
 */
const bandsOpenOnComparison = (
  data: TrendSchema["chart"]["data"],
  leadField: string,
  comparisonField: string | undefined,
): boolean => {
  if (comparisonField === undefined || data.length === 0) return false;

  return missing(data[0][leadField]) && missing(data[data.length - 1][comparisonField]);
};

const Totals = styled("dl")(({ theme }) => ({
  margin: 0,
  paddingBottom: theme.fieldSalesTwo.spacing[8],
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  columnGap: theme.fieldSalesTwo.spacing[40],
}));

const SeriesLabel = styled(Label)(({ theme }) => ({
  display: "flex",
  gap: theme.fieldSalesTwo.spacing[8],
}));

const ValueGroup = styled("dd")(({ theme }) => ({
  margin: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[4],
}));

const RangeLabel = styled(SubLabel)(({ theme }) => ({
  fontSize: theme.fieldSalesTwo.font.size["12"],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

interface SeriesTotal {
  field: string;
  label: string;
  value: string;
  range: string;
  barFill: string;
}

function TrendTotals({ totals }: { totals: SeriesTotal[] }) {
  return (
    <Totals>
      {totals.map((total) => (
        <FooterCol key={total.field}>
          <SeriesLabel as="dt">
            <Marker aria-hidden style={{ background: total.barFill }} />
            {total.label}
          </SeriesLabel>
          <ValueGroup>
            <KpiValue>{total.value}</KpiValue>
            <RangeLabel>{total.range}</RangeLabel>
          </ValueGroup>
        </FooterCol>
      ))}
    </Totals>
  );
}

interface TrendProps {
  part: TrendSchema;
}

export function Trend({ part }: TrendProps) {
  const showsKpiDesign = useFlagWithContext("q4-new-card-types", false);

  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.trend");

  const { chart, dateRange } = part;
  const { locale, currency, leadSeries, comparisonSeries } = useChartDisplay(chart);

  const bandsStart = bandsOpenOnComparison(chart.data, leadSeries.field, comparisonSeries?.field)
    ? dateRange.comparison.start
    : dateRange.current.start;
  const periodStarts = chart.data.map((_, index) => toIsoDate(addMonthsUtc(new Date(bandsStart), index)));

  // Key each bar on its period start, unique per bar (see TrendBarChart's xField); the label field drives the tooltip title.
  const rows = chart.data.map((row, index) => ({ ...row, [PERIOD_KEY]: periodStarts[index] }));
  const axisLabels = chart.data.map((row) => String(row[chart.x.field] ?? ""));

  const leadTotal = chart.data.reduce((sum, row) => sum + Number(row[leadSeries.field] ?? 0), 0);

  const comparisonTotal = comparisonSeries
    ? chart.data.reduce((sum, row) => sum + Number(row[comparisonSeries.field] ?? 0), 0)
    : 0;

  const hasComparison = comparisonSeries !== undefined && comparisonTotal > 0;

  const computedTrendPercent = hasComparison
    ? Math.round(((leadTotal - comparisonTotal) / comparisonTotal) * 100)
    : null;

  const trendPercent = part.trend ?? computedTrendPercent;

  const chartView = (
    <TrendBarChart
      data={rows}
      xField={PERIOD_KEY}
      leadSeries={leadSeries}
      comparisonSeries={hasComparison ? comparisonSeries : undefined}
      periodStarts={periodStarts}
      axisLabels={axisLabels}
      currency={currency}
      locale={locale}
      tooltipLabelField={chart.x.field}
    />
  );

  if (showsKpiDesign) {
    const leadEntry: SeriesTotal = {
      field: leadSeries.field,
      label: leadSeries.label,
      value: formatCurrency(leadTotal, currency, locale),
      range: formatNumericDayMonthRange(dateRange.current, locale),
      barFill: LEAD_SERIES_FILL,
    };
    const totals: SeriesTotal[] = hasComparison
      ? [
          {
            field: comparisonSeries.field,
            label: comparisonSeries.label,
            value: formatCurrency(comparisonTotal, currency, locale),
            range: formatNumericDayMonthRange(dateRange.comparison, locale),
            barFill: COMPARISON_SERIES_FILL,
          },
          leadEntry,
        ]
      : [leadEntry];

    return (
      <Card bleed>
        <Section>
          <Header>
            {hasComparison && hasTrend(trendPercent) && (
              <MetaLabel>
                <SentimentColor sentiment={trendSentiment(trendPercent)}>
                  {formatPercentDelta(trendPercent, locale)}
                </SentimentColor>
              </MetaLabel>
            )}
            <CardHeadline>{part.headline}</CardHeadline>
          </Header>
        </Section>
        <Divider />
        <Section>
          <TrendTotals totals={totals} />
        </Section>
        <Section>{chartView}</Section>
      </Card>
    );
  }

  return (
    <Card>
      <Header>
        <MetaLabel>{t("meta", { range: formatMonthYearRange(dateRange.current, locale) })}</MetaLabel>
        <CardHeadline>{formatCurrency(leadTotal, currency, locale)}</CardHeadline>
      </Header>
      <Divider />
      {hasComparison && (
        <TrendComparison
          label={t("vorperiode")}
          rangeLabel={formatMonthYearRange(dateRange.comparison, locale)}
          value={formatCurrency(comparisonTotal, currency, locale)}
          trendLabel={t("trend")}
          trendPercent={trendPercent}
        />
      )}
      {chartView}
    </Card>
  );
}
